import { useEffect, useRef, useState } from "react";
import { XStack, YStack } from "tamagui";

const NUM_BARS = 10;

// Height in pixels
const STANDARD_HEIGHT = 2; // No voice
const MIN_HEIGHT = 8; // Minimum voice level height
const MAX_HEIGHT = 16; // Maximum voice level height

// Voice frequency range: 300Hz–3400Hz
const VOICE_FREQ_MIN_HZ = 300;
const VOICE_FREQ_MAX_HZ = 3400;

interface VoiceVisualizerProps {
  stream?: MediaStream | null;
}

const VoiceVisualizer = ({ stream }: VoiceVisualizerProps) => {
  const [bars, setBars] = useState<number[]>(
    new Array(NUM_BARS).fill(STANDARD_HEIGHT),
  );
  const animationFrameRef = useRef<number | null>(null);

  useEffect(() => {
    if (!stream) {
      setBars(new Array(NUM_BARS).fill(STANDARD_HEIGHT));
      return;
    }

    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.8;

    const source = audioContext.createMediaStreamSource(stream);
    source.connect(analyser);

    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    const updateBars = () => {
      analyser.getByteFrequencyData(dataArray);

      const sampleRate = audioContext.sampleRate;
      const binCount = dataArray.length;
      const minBin = Math.floor(
        (VOICE_FREQ_MIN_HZ / (sampleRate / 2)) * binCount,
      );
      const maxBin = Math.ceil(
        (VOICE_FREQ_MAX_HZ / (sampleRate / 2)) * binCount,
      );
      const voiceBins = Array.from(dataArray.slice(minBin, maxBin));
      const bandSize = Math.max(1, Math.floor(voiceBins.length / NUM_BARS));

      const newBars = Array.from({ length: NUM_BARS }, (_, i) => {
        const band = voiceBins.slice(i * bandSize, i * bandSize + bandSize);
        const avg = band.reduce((s, v) => s + v, 0) / band.length;
        const normalized = avg / 255;
        if (normalized < 0.1) return STANDARD_HEIGHT;
        return MIN_HEIGHT + (MAX_HEIGHT - MIN_HEIGHT) * Math.min(normalized, 1);
      });

      setBars(newBars);
      animationFrameRef.current = requestAnimationFrame(updateBars);
    };

    updateBars();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      audioContext.close();
    };
  }, [stream]);

  return (
    <XStack height={MAX_HEIGHT} gap="$50" alignItems="center">
      {bars.map((barHeight, index) => (
        <YStack
          key={index}
          height={barHeight}
          transition="standard"
          width="$50"
          backgroundColor="$textSecondary"
        />
      ))}
    </XStack>
  );
};

export default VoiceVisualizer;
